import React, { useState } from "react";
import { api } from "../lib/api";
import { Save, RotateCcw, Check } from "lucide-react";

const TYPE_OPTIONS = ["Whole Home", "Kitchen", "Bathroom", "Primary Suite", "Fixer-Upper Transformation"];

function ProjectCard({ project, onSaved }) {
  const [form, setForm] = useState(project);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/admin/projects/${project.id}`, {
        name: form.name, location: form.location, type: form.type,
        challenge: form.challenge, vision: form.vision, transformation: form.transformation,
      });
      setForm(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      onSaved && onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/admin/projects/${project.id}`);
      setForm(res.data);
      onSaved && onSaved();
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-cream/50 border border-cream-dark px-3 py-2.5 font-sans text-sm text-navy focus:border-gold focus:outline-none transition-colors";
  const labelCls = "font-sans text-[10px] tracking-wide-nav uppercase text-charcoal/55 mb-1.5 block";

  return (
    <div className="bg-white border border-cream-dark p-6 md:p-7">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className={labelCls}>Project Title</label>
          <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Location</label>
          <input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Project Type</label>
          <select className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)}>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            {!TYPE_OPTIONS.includes(form.type) && <option value={form.type}>{form.type}</option>}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>The Challenge</label>
          <textarea rows={3} className={inputCls + " resize-none"} value={form.challenge} onChange={(e) => set("challenge", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>The Vision</label>
          <textarea rows={3} className={inputCls + " resize-none"} value={form.vision} onChange={(e) => set("vision", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>The Transformation</label>
          <textarea rows={3} className={inputCls + " resize-none"} value={form.transformation} onChange={(e) => set("transformation", e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-5">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-2.5 font-sans text-[11px] tracking-wide-nav uppercase hover:bg-gold hover:text-navy transition-colors disabled:opacity-60">
          {saved ? <><Check size={14} /> Saved</> : <><Save size={14} /> Save Changes</>}
        </button>
        {(form.overridden?.length > 0 || form.dirty) && (
          <button onClick={reset} disabled={busy} className="inline-flex items-center gap-2 text-charcoal/60 px-3 py-2.5 font-sans text-[11px] tracking-wide-nav uppercase hover:text-navy transition-colors">
            <RotateCcw size={14} /> Reset to Default
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProjectManager({ projects, onChange }) {
  return (
    <div className="space-y-6">
      {projects.map((p) => (
        <ProjectCard key={p.id} project={p} onSaved={onChange} />
      ))}
    </div>
  );
}
